import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Task } from '@/src/renderer/lib/db/models'
import type { StagedAction } from './useClarifyState'
import { ClarifyEntryCard } from './ClarifyEntryCard'
import './ClarifyEntryList.scss'

// ──────────────────────────────────────────────────────────────────────────────
// Slot Y-positions (distance from top of scroll viewport)
// ──────────────────────────────────────────────────────────────────────────────
// 2px top inset keeps the back-stack's top edge off the scrollport's clip
// boundary — otherwise sub-pixel borders/shadows get cut by `overflow: auto`.
const SLOT_TOP_INSET = 0
const SLOT_BACK_Y = SLOT_TOP_INSET          // Back-stack card sticks here
const SLOT_FRONT_Y = SLOT_TOP_INSET + 14    // Front-stack (14px below back)
const STACK_ACTIVE_GAP = 14   // visual gap from each card's TOP to the next card's top
// Active slot = front-top + 14px → active card visually sits 14px below the
// front-stack card's top edge and (with higher z-index) covers the rest of
// the front-stack card's body. Only the top 14px of front peeks above active.
const SLOT_ACTIVE_Y = SLOT_FRONT_Y + STACK_ACTIVE_GAP  // = 30

// ──────────────────────────────────────────────────────────────────────────────
// Card widths (horizontal inset used for stack peek effect)
// ──────────────────────────────────────────────────────────────────────────────
const LIST_WIDTH = 225
const ACTIVE_WIDTH = 225
const FRONT_WIDTH = 213  // 6px inset per side
const BACK_WIDTH = 201   // 12px inset per side

// ──────────────────────────────────────────────────────────────────────────────
// Scroll math
// ──────────────────────────────────────────────────────────────────────────────
// Fixed pitch between card positions along the scroll axis. Each "unit" of
// scroll = one card deal. Kept constant so the cardPos math is uniform even
// if individual cards render taller/shorter than the pitch.
const CARD_PITCH = 88
// Fade distance (in card-position units) for cards retreating past the back slot.
const FADE_LENGTH = 0.5

// ──────────────────────────────────────────────────────────────────────────────

export interface ClarifyEntryListProps {
  queue: Task[]
  activeTaskId: string | null
  staged: Map<string, StagedAction>
  stackIds: string[] // kept in props for API stability (legacy) — not used here
  onSelectActive: (taskId: string) => void
}

interface SlotState {
  y: number
  width: number
  opacity: number
  zIndex: number
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

// Lerp factor for the wheel smooth-scroll. Each rAF frame, current scroll
// moves this fraction of the distance toward the target. Higher = snappier,
// lower = silkier. 0.15 matches the continuous feel the user liked for the
// wheel gesture — do not touch without an explicit ask.
const SCROLL_LERP = 0.15

// Fixed duration for click/staging snaps. Uses cubic ease-out (not ease-in-out
// and not the wheel's exponential decay): the motion starts at full speed and
// decelerates gently into the target. Important for held arrow-key nav — each
// key-repeat (~50ms) resets the snap; with an ease-in curve the first 10–15 %
// of the duration barely moves (derivative near 0), so rapid repeats stutter.
// Ease-out starts with derivative 3, so every repeat advances noticeably.
const SNAP_DURATION_MS = 280

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

// ──────────────────────────────────────────────────────────────────────────────
// Stack-position-driven card colours
// ──────────────────────────────────────────────────────────────────────────────
// Front-stack (p = -1): bg #F8F7F6, border #E5E5E5
// Back-stack  (p = -2): bg #FBFBFB, border #F2F2F2
// Queue/active (p ≥ 0): no inline colour — CSS defaults (white / --base-border
// for upcoming, stone-100 for staged-inline) take over via var fallback.
type RGB = [number, number, number]
const FRONT_BG: RGB = [248, 247, 246]   // #F8F7F6
const BACK_BG: RGB = [251, 251, 251]    // #FBFBFB
const FRONT_BORDER: RGB = [229, 229, 229] // #E5E5E5
const BACK_BORDER: RGB = [242, 242, 242]  // #F2F2F2
// "Default" = the CSS baseline that non-stack cards use. Matches --card /
// --base-border so the p=0 boundary blends smoothly into the queue look.
const DEFAULT_BG: RGB = [255, 255, 255]
const DEFAULT_BORDER: RGB = [229, 229, 229]

function lerpRgb(a: RGB, b: RGB, t: number): RGB {
  const clamped = Math.max(0, Math.min(1, t))
  return [
    a[0] + (b[0] - a[0]) * clamped,
    a[1] + (b[1] - a[1]) * clamped,
    a[2] + (b[2] - a[2]) * clamped,
  ]
}

function rgbString(c: RGB): string {
  return `rgb(${Math.round(c[0])}, ${Math.round(c[1])}, ${Math.round(c[2])})`
}

function computeStackColors(p: number): { bg: string; border: string } | null {
  if (p >= 0) return null
  let bg: RGB, border: RGB
  if (p >= -1) {
    // active → front
    const t = -p
    bg = lerpRgb(DEFAULT_BG, FRONT_BG, t)
    border = lerpRgb(DEFAULT_BORDER, FRONT_BORDER, t)
  } else if (p >= -2) {
    // front → back
    const t = -p - 1
    bg = lerpRgb(FRONT_BG, BACK_BG, t)
    border = lerpRgb(FRONT_BORDER, BACK_BORDER, t)
  } else {
    // past back (fading) — keep back colours
    bg = BACK_BG
    border = BACK_BORDER
  }
  return { bg: rgbString(bg), border: rgbString(border) }
}

/**
 * Compute the visual state for a given card, parameterised by its index and
 * the current (continuous) scroll progress `s = scrollTop / CARD_PITCH`.
 *
 * Two phases:
 *  - **Emergence (s < 1)**: the very first scroll tick. At s=0 the active
 *    card (idx 0) sits flush at y=0 (no stack above yet). As the user
 *    scrolls to s=1, card 0 morphs into the front-stack slot (y=14, mid
 *    width) and card 1 takes over as the new active (y=75, full width).
 *  - **Stable (s ≥ 1)**: every additional tick shifts the whole deck up.
 *    Active stays at SLOT_ACTIVE_Y=75, cards crossing p=-1 and p=-2 visit
 *    the fixed stack slots, and cards past -2 fade out.
 */
function computeSlot(idx: number, s: number): SlotState {
  // Z-index rule: every later-indexed card always stacks visually above the
  // earlier ones. This is the "incoming always on top of previous" behaviour
  // the card-deal metaphor needs — avoids a mid-morph z-index flip where the
  // approaching card would briefly slide under the receding one.
  const zIndex = idx

  if (s < 1) {
    // Emergence phase.
    if (idx === 0) {
      return {
        y: SLOT_FRONT_Y * s, // 0 → 14
        width: lerp(ACTIVE_WIDTH, FRONT_WIDTH, s),
        opacity: 1,
        zIndex,
      }
    }
    // idx >= 1: queue card approaching (or taking over) active slot.
    const startY = idx * CARD_PITCH
    const endY = SLOT_ACTIVE_Y + (idx - 1) * CARD_PITCH
    return {
      y: lerp(startY, endY, s),
      width: ACTIVE_WIDTH,
      opacity: 1,
      zIndex,
    }
  }

  // Stable phase.
  const p = idx - s
  if (p >= 0) {
    // Active + queue (downward of active).
    return {
      y: SLOT_ACTIVE_Y + p * CARD_PITCH,
      width: ACTIVE_WIDTH,
      opacity: 1,
      zIndex,
    }
  }
  if (p >= -1) {
    // Active → front-stack morph.
    const t = -p
    return {
      y: lerp(SLOT_ACTIVE_Y, SLOT_FRONT_Y, t),
      width: lerp(ACTIVE_WIDTH, FRONT_WIDTH, t),
      opacity: 1,
      zIndex,
    }
  }
  if (p >= -2) {
    // Front → back morph.
    const t = -p - 1
    return {
      y: lerp(SLOT_FRONT_Y, SLOT_BACK_Y, t),
      width: lerp(FRONT_WIDTH, BACK_WIDTH, t),
      opacity: 1,
      zIndex,
    }
  }
  // p < -2: fading out past the back slot. Stay at SLOT_BACK_Y — don't lift
  // upward; the card should just dissolve in place.
  const fadeT = Math.min(1, (-p - 2) / FADE_LENGTH)
  return {
    y: SLOT_BACK_Y,
    width: BACK_WIDTH,
    opacity: 1 - fadeT,
    zIndex,
  }
}

/**
 * Linke Spalte im Clarify-Mode — Kartenstapel-Modell.
 *
 * Alle Cards werden in einer einzigen Schicht gerendert. Ihre Position,
 * Breite und Opacity werden kontinuierlich aus der Scroll-Position abgeleitet.
 * Scrollen = Karten legen: die aktive Card rutscht in die Front-Stack-Position,
 * die bisherige Front rückt zur Back, die alte Back fadet weg, die nächste
 * Queue-Card wird zur neuen aktiven Card. Alles ohne Stack-Overlay oder
 * Doppel-Rendering.
 */
export function ClarifyEntryList({
  queue,
  activeTaskId,
  staged,
  onSelectActive,
}: ClarifyEntryListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const isAutoScrollingRef = useRef(false)
  // Unified smooth-scroll: both wheel gestures and the programmatic auto-snap
  // drive this ref's setter, which runs a single rAF lerp loop. Populated by
  // the setup effect below.
  const smoothScrollToRef = useRef<((target: number) => void) | null>(null)
  // When the scroll handler is the one that pushed a new activeTaskId (from
  // rounding the current scroll position), skip the next auto-scroll so we
  // don't fight the user's manual scroll gesture with a programmatic snap.
  const skipNextAutoScrollRef = useRef(false)
  // Keep a ref to the latest activeTaskId so the scroll handler (registered
  // once) always reads the fresh value without re-registering on every change.
  const activeTaskIdRef = useRef(activeTaskId)
  activeTaskIdRef.current = activeTaskId
  // Latest clamp-ceiling for the smooth-scroll loop (registered once).
  const maxScrollTopRef = useRef(0)

  const [scrollTop, setScrollTop] = useState(0)
  const [clientHeight, setClientHeight] = useState(0)

  const activeIdx = useMemo(
    () => (activeTaskId ? queue.findIndex(t => t.id === activeTaskId) : 0),
    [activeTaskId, queue],
  )

  // Scroll-content height = max scrollTop needed + clientHeight. This caps
  // native scroll exactly at `(queue.length - 1) * CARD_PITCH` so the user
  // cannot scroll past the last card into an empty stack.
  const maxScrollTop = Math.max(0, queue.length - 1) * CARD_PITCH
  const contentHeight = maxScrollTop + Math.max(clientHeight, 1)
  maxScrollTopRef.current = maxScrollTop

  // Register scroll listener ONCE — reading activeTaskId via ref keeps the
  // handler stable across renders, which also prevents a subtle race where
  // re-registering the listener on every activeTaskId change would fire a
  // synthetic initial invocation that fought the pending auto-scroll.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handler = () => {
      setScrollTop(prev => (prev === el.scrollTop ? prev : el.scrollTop))
      if (isAutoScrollingRef.current) return
      const rawIdx = el.scrollTop / CARD_PITCH
      const nearestIdx = Math.round(rawIdx)
      const clamped = Math.max(0, Math.min(queue.length - 1, nearestIdx))
      const nextId = queue[clamped]?.id
      if (nextId && nextId !== activeTaskIdRef.current) {
        // The user drove this change via manual scroll — don't let the
        // auto-scroll effect then snap us to the target (that creates the
        // "jump" at the mid-transition point the user reported).
        skipNextAutoScrollRef.current = true
        onSelectActive(nextId)
      }
    }
    el.addEventListener('scroll', handler, { passive: true })
    return () => el.removeEventListener('scroll', handler)
  }, [queue, onSelectActive])

  // Track the viewport height so the scroll-content can match exactly.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => setClientHeight(el.clientHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Two separate animation paths share one rAF slot. The wheel path keeps
  // the Lenis-style exponential lerp exactly as it was (don't touch — the
  // user tuned this and likes how it feels). The snap path (click / staging
  // / keyboard activation) runs a time-based cubic ease-in-out instead, so
  // the approach and arrival are symmetric and don't telegraph the
  // instant variant-class flip on the newly-active card.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    let current = el.scrollTop
    let target = el.scrollTop
    let rafId: number | null = null
    let mode: 'idle' | 'wheel' | 'snap' = 'idle'

    // Snap state (time-based — filled in by smoothScrollToRef).
    let snapStart = 0
    let snapFrom = 0
    let snapTo = 0

    const clamp = (v: number): number =>
      Math.max(0, Math.min(maxScrollTopRef.current, v))

    const cancelRaf = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
        rafId = null
      }
    }

    // Wheel tick — exponential lerp with a minimum step floor on the tail.
    // Without the floor, once the raw lerp step drops below ~0.3 px/frame the
    // motion lingers for another 10+ frames of imperceptible creep before
    // snapping; users perceive the scroll as "stopped" during that creep and
    // then see the final `current = target` snap (up to 0.3 px) about a
    // second after they lifted their finger. The 0.5 px floor keeps the tail
    // moving at one device pixel (Retina) per frame so it terminates quickly
    // and invisibly.
    const WHEEL_TAIL_MIN_STEP = 0.5
    const tickWheel = () => {
      if (mode !== 'wheel') {
        rafId = null
        return
      }
      const diff = target - current
      const rawStep = diff * SCROLL_LERP
      const step =
        Math.abs(rawStep) < WHEEL_TAIL_MIN_STEP
          ? Math.sign(diff) * WHEEL_TAIL_MIN_STEP
          : rawStep
      if (Math.abs(step) >= Math.abs(diff)) {
        current = target
        el.scrollTop = target
        isAutoScrollingRef.current = false
        mode = 'idle'
        rafId = null
        return
      }
      current += step
      el.scrollTop = current
      rafId = requestAnimationFrame(tickWheel)
    }

    // Snap tick — time-based cubic ease-in-out.
    const tickSnap = (now: number) => {
      if (mode !== 'snap') {
        rafId = null
        return
      }
      const t = Math.min(1, (now - snapStart) / SNAP_DURATION_MS)
      const eased = easeOutCubic(t)
      current = snapFrom + (snapTo - snapFrom) * eased
      el.scrollTop = current
      if (t >= 1) {
        current = snapTo
        target = snapTo
        el.scrollTop = snapTo
        isAutoScrollingRef.current = false
        mode = 'idle'
        rafId = null
        return
      }
      rafId = requestAnimationFrame(tickSnap)
    }

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      // User gesture trumps any pending programmatic snap.
      isAutoScrollingRef.current = false
      // Re-sync from actual scrollTop in case something external moved it
      // between frames, then accumulate the new delta into target.
      current = el.scrollTop
      target = clamp(target + e.deltaY)
      if (mode !== 'wheel') {
        cancelRaf()
        mode = 'wheel'
        rafId = requestAnimationFrame(tickWheel)
      } else if (rafId === null) {
        rafId = requestAnimationFrame(tickWheel)
      }
    }

    smoothScrollToRef.current = (t: number) => {
      const clamped = clamp(t)
      const from = el.scrollTop
      if (Math.abs(clamped - from) < 0.3) return
      snapFrom = from
      snapTo = clamped
      snapStart = performance.now()
      current = from
      target = clamped
      isAutoScrollingRef.current = true
      cancelRaf()
      mode = 'snap'
      rafId = requestAnimationFrame(tickSnap)
    }

    el.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      el.removeEventListener('wheel', onWheel)
      cancelRaf()
      smoothScrollToRef.current = null
    }
  }, [])

  // When activeTaskId changes (click / staging / keyboard), smooth-scroll to
  // align it with the active slot. The `isAutoScrollingRef` flag tells the
  // scroll handler to ignore the resulting scroll events so it doesn't
  // re-select a different card mid-animation.
  useEffect(() => {
    // The previous change came from the scroll handler itself — user already
    // moved the scroll there, no need (and harmful) to snap again.
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false
      return
    }
    if (activeIdx < 0) return
    smoothScrollToRef.current?.(activeIdx * CARD_PITCH)
  }, [activeTaskId, activeIdx])

  const handleCardClick = useCallback(
    (taskId: string) => {
      onSelectActive(taskId)
    },
    [onSelectActive],
  )

  const scrollProgress = scrollTop / CARD_PITCH
  // Bottom fade is shown only while more queue cards remain below the active
  // card; the last card doesn't need a fade indicator.
  const showBottomFade = activeIdx < queue.length - 1

  return (
    <div className="clarify-entry-list">
      <div className="clarify-entry-list__scroll" ref={scrollRef}>
        <div
          className="clarify-entry-list__scroll-content"
          style={{ height: contentHeight }}
        >
          {queue.map((task, idx) => {
            const slot = computeSlot(idx, scrollProgress)
            const p = idx - scrollProgress
            const colors = computeStackColors(p)
            const isActive = task.id === activeTaskId
            const stagedAction = staged.get(task.id)
            const variant = isActive
              ? 'active'
              : stagedAction
                ? 'staged-inline'
                : 'upcoming'
            const translateY = scrollTop + slot.y
            const left = (LIST_WIDTH - slot.width) / 2
            // `translate3d(0, Y, 0)` (not plain `translateY(Y)`) pins the
            // element into a 3D rendering context so the browser keeps a
            // stable compositor layer alive for it.
            const slotStyle: React.CSSProperties = {
              left: `${left}px`,
              width: `${slot.width}px`,
              opacity: slot.opacity,
              zIndex: slot.zIndex,
              transform: `translate3d(0, ${translateY}px, 0)`,
            }
            if (colors) {
              ;(slotStyle as Record<string, string>)['--card-bg'] = colors.bg
              ;(slotStyle as Record<string, string>)['--card-border'] = colors.border
            }
            return (
              <div
                key={task.id}
                className="clarify-entry-list__slot"
                style={slotStyle}
              >
                <ClarifyEntryCard
                  task={task}
                  variant={variant}
                  stagedAction={stagedAction}
                  onClick={() => handleCardClick(task.id)}
                />
              </div>
            )
          })}
        </div>
      </div>
      {showBottomFade && (
        <div className="clarify-entry-list__fade-bottom" aria-hidden="true" />
      )}
    </div>
  )
}
